import React from "react";
import { Handle, Position, useReactFlow } from "reactflow";
import { useTranslation } from '../i18n/useTranslation';
import { useGlobalState } from '../i18n/GlobalStateContext';
import { MicrophoneMute, OpenNewWindow } from 'iconoir-react';

//custome node
function PromptNode({ data, selected }) {
  const t = useTranslation();
  const { getNodes, getEdges, getNode } = useReactFlow();
  const { constructVoiceWizardUrl, domainUrl, agentId } = useGlobalState();
  
  const hasSystemInstruction = data.promptSystemInstruction && data.promptSystemInstruction.trim() !== '';
  const isMuted = data.muteUser;

  // Function to get phone number from canvas
  const getPhoneNumberFromCanvas = () => {
    const allNodes = getNodes();
    const numberNode = allNodes.find(node => node.type === 'numbernode');
    return numberNode?.data?.phone_number || null;
  };

  // Function to check if this prompt is the initial prompt (connected from number node)
  const isInitialPrompt = () => {
    const edges = getEdges();
    const currentNodeId = `prompt_${data.promptId}`;
    
    if (!currentNodeId) return false;
    
    // Check if there's an edge from a number node to this prompt
    return edges.some(edge => {
      const sourceNode = getNode(edge.source);
      return sourceNode?.type === 'numbernode' && edge.target === currentNodeId;
    });
  };

  // Function to handle voice wizard button click
  const handleVoiceWizardClick = () => {
    const phoneNumber = getPhoneNumberFromCanvas();
    const promptId = data.promptId;
    
    if (!phoneNumber) {
      alert('No phone number found in canvas. Please add a phone number node.');
      return;
    }
    
    if (!promptId) {
      alert('No prompt ID found for this node.');
      return;
    }

    const isInitial = isInitialPrompt();
    const voiceWizardUrl = constructVoiceWizardUrl(promptId, phoneNumber, isInitial);
    
    if (voiceWizardUrl) {
      window.open(voiceWizardUrl, '_blank');
    } else {
      alert('Unable to construct voice wizard URL. Please check configuration.');
    }
  };

  // Check if voice wizard should be available
  const isVoiceWizardAvailable = domainUrl && agentId && data.promptId && getPhoneNumberFromCanvas()

  return (
    <div
      className={`
         w-[200px] rounded-lg bg-white shadow-lg overflow-hidden
        transition-all duration-200 ease-in-out
        hover:bg-gray-200 
        ${selected ? "ring-2 ring-green-500 ring-opacity-70" : ""}
      `}
    >
      <div className="flex flex-col">
        <div className="bg-gradient-to-r from-green-600 to-green-500 px-4 py-2 flex justify-between items-center">
          <div className="text-white text-sm font-medium">
            {t('promptNode.title')}
          </div>
          <div className="flex items-center space-x-1">
            {hasSystemInstruction && (
              <div 
                className="text-xs bg-yellow-500 text-white px-1.5 py-0.5 rounded-full" 
                title={t('promptNode.containsStickyInstruction')}
              >
                S
              </div>
            )}
            {isMuted && (
              <div 
                className="text-white" 
                title={t('muteUser')}
              >
                <MicrophoneMute height={16} width={16} />
              </div>
            )}
            {/* Voice Wizard Icon */}
            {isVoiceWizardAvailable && (
              <button
                onClick={handleVoiceWizardClick}
                className="text-white hover:text-green-200 transition-colors"
                title="Open voice wizard for this prompt"
              >
                <OpenNewWindow height={16} width={16} />
              </button>
            )}
          </div>
        </div>
        
        <div className="p-4 space-y-2">
          <div className="text-sm text-gray-700">
            <span className="font-medium">{t('name')}: </span>
            {data.text || t('promptNode.unnamedPrompt')}
          </div>
          
          {data.prompt && (
            <div className="text-xs text-gray-500 line-clamp-4">
              <span className="font-medium">{t('promptNode.content')}: </span>
              {data.prompt}
            </div>
          )}

          {hasSystemInstruction && (
            <div className="text-xs text-gray-500 line-clamp-2">
              <span className="font-medium">{t('promptNode.systemInstruction')}: </span>
              {data.promptSystemInstruction}
            </div>
          )}
        </div>
      </div>

      <Handle
        id="a"
        type="target"
        position={Position.Top}
        className="w-3 h-3 rounded-full bg-green-500 border-2 border-white -top-1.5"
      />
      <Handle
        id="b"
        type="source"
        position={Position.Bottom}
        className="w-3 h-3 rounded-full bg-green-500 border-2 border-white -bottom-1.5"
      />
      <Handle
        id="c"
        type="source"
        position={Position.Right}
        className="w-3 h-3 rounded-full bg-green-500 border-2 border-white"
      />
    </div>
  );
}

export default PromptNode;